import React, { Fragment } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../common/Icon';
import { BaseText } from '../../common/BaseText';
import { Separator } from '../../common/Separator';
import { parseApiError } from '@utils';
import { Comment } from '@models/Comments/CommentModel';
import { ToastService } from '@services';
import { useAsyncInit } from '@hooks';
import { CommentList } from '@models/Comments/CommentListModel';
import { Loader } from '../../common/Loader';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import { CommentViewList } from './CommentViewList';
import { CommentViewLeftSpace } from './CommentItemLeftSpace';
import type { ICommentListStore } from '../CommentList';
import type { CommentInputSizeEnum } from '../CommentInput';
import { User } from '@models/Viewer/UserModel';

const Container = styled(View)({
  flex: 1,
});

const LoaderContainer = styled(View)({
  flex: 1,
  minHeight: 100,
  alignItems: 'center',
  justifyContent: 'center',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
});

const ActionTouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
});

const ActionText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
});

interface ICommentRepliesProps {
  list: CommentList;
  onPressReply(item: Comment): void;
  onPressEdit(item: Comment): void;
  commentListStore: ICommentListStore;
  commentInputSize?: CommentInputSizeEnum;
  onPressUser?(user: User): void;
}

export const CommentReplies = observer<ICommentRepliesProps>(
  ({
    list,
    onPressReply,
    onPressEdit,
    commentListStore,
    commentInputSize,
    onPressUser,
  }) => {
    const { t } = useTranslation();

    useAsyncInit(() => fetch());

    async function fetch() {
      try {
        await list.fetch.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    async function fetchMore() {
      try {
        await list.fetchMore.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    if (list.fetch.inProgress) {
      return (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      );
    }

    const canFetchMore =
      !list.hasNoMore &&
      !list.fetch.inProgress &&
      !list.fetchMore.inProgress;

    return (
      <Container>
        <CommentViewList
          list={list}
          onPressReply={onPressReply}
          onPressEdit={onPressEdit}
          commentListStore={commentListStore}
          commentInputSize={commentInputSize}
          onPressUser={onPressUser}
        />

        {canFetchMore && (
          <Fragment>
            <Separator size={['$5', '$6']} />
            <RowContainer>
              <CommentViewLeftSpace />
              <ActionTouchableContainer
                onPress={fetchMore}
                type={TouchableTypeEnum.Opacity}
              >
                <Icon name="small-arrow-down-light" size={20} />
                <Separator size="$5" vertical />
                <ActionText>
                  {t('common.commentItem.loadMoreAnswers')}
                </ActionText>
              </ActionTouchableContainer>
            </RowContainer>
          </Fragment>
        )}

        {list.fetchMore.inProgress && (
          <LoaderContainer>
            <Loader size="large" />
          </LoaderContainer>
        )}
      </Container>
    );
  },
);
